import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { uid } from '@instructure/uid';
import { themeable } from '@instructure/ui-themeable';
import { testable } from '@instructure/ui-testable';
import { omitProps } from '@instructure/ui-react-utils';
import { isActiveElement } from '@instructure/ui-dom-utils';
var styles = {
  componentId: 'Vmatu',
  template: function template(theme) {
    return "\n\n.Vmatu_bGBk{position:relative;width:100%}\n\n.Vmatu_bGBk:hover{cursor:default}\n\n.Vmatu_bOnW{all:initial;animation:none 0s ease 0s 1 normal none running;backface-visibility:visible;background:transparent none repeat 0 0/auto auto padding-box border-box scroll;border:medium none currentColor;border-collapse:separate;border-image:none;border-radius:0;border-spacing:0;bottom:auto;box-shadow:none;box-sizing:content-box;caption-side:top;clear:none;clip:auto;color:#000;column-count:auto;column-fill:balance;column-gap:normal;column-rule:medium none currentColor;column-span:1;column-width:auto;columns:auto;content:normal;counter-increment:none;counter-reset:none;cursor:auto;direction:ltr;direction:inherit;display:inline;display:block;empty-cells:show;float:none;font-family:serif;font-size:medium;font-stretch:normal;font-style:normal;font-variant:normal;font-weight:400;height:auto;hyphens:none;left:auto;letter-spacing:normal;line-height:normal;list-style:disc outside none;margin:0;max-height:none;max-width:none;min-height:0;min-width:0;opacity:1;orphans:2;outline:medium none invert;overflow:visible;overflow-x:visible;overflow-y:visible;padding:0;page-break-after:auto;page-break-before:auto;page-break-inside:auto;perspective:none;perspective-origin:50% 50%;position:static;right:auto;tab-size:8;table-layout:auto;text-align:left;text-align:start;text-align-last:auto;text-decoration:none;text-indent:0;text-shadow:none;text-transform:none;top:auto;transform:none;transform-origin:50% 50% 0;transform-style:flat;transition:none 0s ease 0s;unicode-bidi:normal;vertical-align:baseline;visibility:visible;white-space:normal;widows:2;width:auto;word-spacing:normal;z-index:auto}\n\n[dir=ltr] .Vmatu_bOnW{text-align:left}\n\n[dir=rtl] .Vmatu_bOnW{text-align:right}\n\n.Vmatu_cwos,input.Vmatu_cwos[type=radio]{font-size:inherit;left:0;line-height:inherit;margin:0;opacity:0.0001;padding:0;position:absolute;top:0;width:auto}\n\n.Vmatu_ywdX{opacity:0.5}\n\n.Vmatu_ywdX:hover{cursor:not-allowed}\n\n.Vmatu_eXrk{display:inline-block;vertical-align:middle;width:auto}\n\n.Vmatu_blJt{color:".concat(theme.labelColor || 'inherit', ";flex:1 1 auto;font-family:").concat(theme.labelFontFamily || 'inherit', ";font-weight:").concat(theme.labelFontWeight || 'inherit', ";line-height:").concat(theme.labelLineHeight || 'inherit', "}\n\n.Vmatu_cAug .Vmatu_bOnW{align-items:flex-start;display:flex}\n\n.Vmatu_cAug .Vmatu_cSXm{-webkit-margin-end:").concat(theme.simpleFacadeMarginEnd || 'inherit', ";-webkit-margin-start:0;background:").concat(theme.background || 'inherit', ";border:").concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";border-radius:100%;box-sizing:border-box;display:block;flex-shrink:0;margin-inline-end:").concat(theme.simpleFacadeMarginEnd || 'inherit', ";margin-inline-start:0;min-width:1rem;position:relative;transition:all 0.2s ease-out}\n\n[dir=ltr] .Vmatu_cAug .Vmatu_cSXm{margin-left:0;margin-right:").concat(theme.simpleFacadeMarginEnd || 'inherit', "}\n\n[dir=rtl] .Vmatu_cAug .Vmatu_cSXm{margin-left:").concat(theme.simpleFacadeMarginEnd || 'inherit', ";margin-right:0}\n\n.Vmatu_cAug .Vmatu_cSXm:before{border:").concat(theme.focusBorderWidth || 'inherit', " ").concat(theme.focusBorderStyle || 'inherit', " ").concat(theme.focusBorderColor || 'inherit', ";border-radius:100%;box-sizing:border-box;content:\"\";height:calc(100% + 0.75rem);left:-0.375rem;opacity:0;pointer-events:none;position:absolute;top:-0.375rem;transform:scale(0.75);transition:all 0.2s;width:calc(100% + 0.75rem)}\n\n.Vmatu_cAug.Vmatu_doqw .Vmatu_cSXm{height:").concat(theme.simpleFacadeSmallSize || 'inherit', ";width:").concat(theme.simpleFacadeSmallSize || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_doqw .Vmatu_blJt{font-size:").concat(theme.simpleFontSizeSmall || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_doqw .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_cSXm{box-shadow:inset 0 0 0 ").concat(theme.simpleCheckedInsetSmall || 'inherit', " ").concat(theme.hoverBorderColor || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_ycrn .Vmatu_cSXm{height:").concat(theme.simpleFacadeMediumSize || 'inherit', ";width:").concat(theme.simpleFacadeMediumSize || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_ycrn .Vmatu_blJt{font-size:").concat(theme.simpleFontSizeMedium || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_ycrn .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_cSXm{box-shadow:inset 0 0 0 ").concat(theme.simpleCheckedInsetMedium || 'inherit', " ").concat(theme.hoverBorderColor || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_cMDj .Vmatu_cSXm{height:").concat(theme.simpleFacadeLargeSize || 'inherit', ";width:").concat(theme.simpleFacadeLargeSize || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_cMDj .Vmatu_blJt{font-size:").concat(theme.simpleFontSizeLarge || 'inherit', "}\n\n.Vmatu_cAug.Vmatu_cMDj .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_cSXm{box-shadow:inset 0 0 0 ").concat(theme.simpleCheckedInsetLarge || 'inherit', " ").concat(theme.hoverBorderColor || 'inherit', "}\n\n.Vmatu_cAug .Vmatu_cwos:hover+.Vmatu_bOnW .Vmatu_cSXm{border-color:").concat(theme.hoverBorderColor || 'inherit', "}\n\n.Vmatu_cAug .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_cSXm{background:").concat(theme.background || 'inherit', ";border-color:").concat(theme.hoverBorderColor || 'inherit', ";box-shadow:inset 0 0 0 ").concat(theme.controlSize || 'inherit', " ").concat(theme.hoverBorderColor || 'inherit', "}\n\n.Vmatu_cAug .Vmatu_cwos:focus+.Vmatu_bOnW .Vmatu_cSXm{background:").concat(theme.background || 'inherit', "}\n\n.Vmatu_cAug .Vmatu_cwos:focus+.Vmatu_bOnW .Vmatu_cSXm:before{opacity:1;transform:scale(1)}\n\n.Vmatu_cjSw .Vmatu_bOnW{-ms-user-select:none;-webkit-user-select:none;box-sizing:border-box;display:block;position:relative;user-select:none}\n\n.Vmatu_cjSw .Vmatu_blJt{align-items:center;display:flex;line-height:1;min-width:0.0625rem;overflow:hidden;position:relative;text-overflow:ellipsis;text-transform:uppercase;white-space:nowrap;z-index:1}\n\n.Vmatu_cjSw .Vmatu_cSXm{border-radius:").concat(theme.toggleBorderRadius || 'inherit', ";box-shadow:").concat(theme.toggleShadow || 'inherit', ";display:block;height:100%;left:0;top:0;visibility:hidden;width:100%;z-index:1}\n\n.Vmatu_cjSw .Vmatu_cSXm,.Vmatu_cjSw .Vmatu_cSXm:before{box-sizing:border-box;position:absolute}\n\n.Vmatu_cjSw .Vmatu_cSXm:before{border:").concat(theme.focusBorderWidth || 'inherit', " ").concat(theme.focusBorderStyle || 'inherit', " ").concat(theme.focusBorderColor || 'inherit', ";border-radius:calc(").concat(theme.toggleBorderRadius || 'inherit', " + 0.0625rem);content:\"\";height:calc(100% + 0.5rem);left:-0.25rem;opacity:0;top:-0.25rem;transform:scale(0.75);transition:all 0.2s;width:calc(100% + 0.5rem)}\n\n.Vmatu_cjSw.Vmatu_cOXX .Vmatu_cSXm{background-color:").concat(theme.toggleBackgroundSuccess || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_zGXc .Vmatu_cSXm{background-color:").concat(theme.toggleBackgroundDanger || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_eRqw .Vmatu_cSXm{background-color:").concat(theme.toggleBackgroundWarning || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_ksNK .Vmatu_cSXm{background-color:").concat(theme.toggleBackgroundOff || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_doqw .Vmatu_bOnW{height:").concat(theme.toggleSmallHeight || 'inherit', ";padding:0 0.5rem}\n\n.Vmatu_cjSw.Vmatu_doqw .Vmatu_bOnW .Vmatu_blJt{font-size:").concat(theme.toggleSmallFontSize || 'inherit', ";height:").concat(theme.toggleSmallHeight || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_doqw .Vmatu_bOnW .Vmatu_blJt svg{font-size:calc(").concat(theme.toggleSmallFontSize || 'inherit', " + 0.375rem)}\n\n.Vmatu_cjSw.Vmatu_ycrn .Vmatu_bOnW{height:").concat(theme.toggleMediumHeight || 'inherit', ";padding:0 0.875rem}\n\n.Vmatu_cjSw.Vmatu_ycrn .Vmatu_bOnW .Vmatu_blJt{font-size:").concat(theme.toggleMediumFontSize || 'inherit', ";height:").concat(theme.toggleMediumHeight || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_ycrn .Vmatu_bOnW .Vmatu_blJt svg{font-size:calc(").concat(theme.toggleMediumFontSize || 'inherit', " + 0.375rem)}\n\n.Vmatu_cjSw.Vmatu_cMDj .Vmatu_bOnW{height:").concat(theme.toggleLargeHeight || 'inherit', ";padding:0 1rem}\n\n.Vmatu_cjSw.Vmatu_cMDj .Vmatu_bOnW .Vmatu_blJt{font-size:").concat(theme.toggleLargeFontSize || 'inherit', ";height:").concat(theme.toggleLargeHeight || 'inherit', "}\n\n.Vmatu_cjSw.Vmatu_cMDj .Vmatu_bOnW .Vmatu_blJt svg{font-size:calc(").concat(theme.toggleLargeFontSize || 'inherit', " + 0.375rem)}\n\n.Vmatu_cjSw .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_cSXm{visibility:visible}\n\n.Vmatu_cjSw .Vmatu_cwos:checked+.Vmatu_bOnW .Vmatu_blJt{color:").concat(theme.toggleHandleText || 'inherit', "}\n\n.Vmatu_cjSw .Vmatu_cwos:focus+.Vmatu_bOnW .Vmatu_blJt{text-decoration:underline}\n\n.Vmatu_cjSw .Vmatu_cwos:focus+.Vmatu_bOnW .Vmatu_cSXm:before{opacity:1;transform:scale(1)}");
  },
  'root': 'Vmatu_bGBk',
  'control': 'Vmatu_bOnW',
  'input': 'Vmatu_cwos',
  'disabled': 'Vmatu_ywdX',
  'inline': 'Vmatu_eXrk',
  'label': 'Vmatu_blJt',
  'simple': 'Vmatu_cAug',
  'facade': 'Vmatu_cSXm',
  'small': 'Vmatu_doqw',
  'medium': 'Vmatu_ycrn',
  'large': 'Vmatu_cMDj',
  'toggle': 'Vmatu_cjSw',
  'success': 'Vmatu_cOXX',
  'danger': 'Vmatu_zGXc',
  'warning': 'Vmatu_eRqw',
  'off': 'Vmatu_ksNK'
};
import theme from "./theme.js";
/**
---
category: components
---
**/

var RadioInput = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(RadioInput, _Component);

  var _super = _createSuper(RadioInput);

  function RadioInput(props) {
    var _this;

    _classCallCheck(this, RadioInput);

    _this = _super.call(this, props);

    _this.handleClick = function (e) {
      if (_this.props.disabled || _this.props.readOnly) {
        e.preventDefault();
        return;
      }

      _this.props.onClick(e);
    };

    _this.handleChange = function (e) {
      if (_this.props.disabled || _this.props.readOnly) {
        e.preventDefault();
        return;
      }

      if (typeof _this.props.checked === 'undefined') {
        _this.setState({
          checked: !_this.state.checked
        });
      }

      _this.props.onChange(e);
    };

    _this.state = {};

    if (typeof props.checked === 'undefined') {
      _this.state.checked = false;
    }

    _this._defaultId = uid('RadioInput');
    return _this;
  }

  _createClass(RadioInput, [{
    key: "focus",
    value: function focus() {
      this._input.focus();
    }
  }, {
    key: "render",
    value: function render() {
      var _classes,
          _this2 = this;

      var _this$props = this.props,
          disabled = _this$props.disabled,
          readOnly = _this$props.readOnly,
          label = _this$props.label,
          variant = _this$props.variant,
          size = _this$props.size,
          inline = _this$props.inline,
          context = _this$props.context,
          value = _this$props.value,
          name = _this$props.name;
      var props = omitProps(this.props, RadioInput.propTypes);
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.disabled, disabled), _defineProperty(_classes, styles[variant], true), _defineProperty(_classes, styles[context], variant === 'toggle'), _defineProperty(_classes, styles[size], true), _defineProperty(_classes, styles['inline'], inline), _classes);
      return /*#__PURE__*/React.createElement("div", {
        className: classnames(classes)
      }, /*#__PURE__*/React.createElement("input", Object.assign({}, props, {
        id: this.id,
        ref: function ref(c) {
          _this2._input = c;
        },
        value: value,
        name: name,
        checked: this.checked,
        type: "radio",
        className: styles.input,
        disabled: disabled || readOnly,
        "aria-disabled": disabled || readOnly ? 'true' : null,
        onChange: this.handleChange,
        onClick: this.handleClick
      })), /*#__PURE__*/React.createElement("label", {
        className: styles.control,
        htmlFor: this.id
      }, /*#__PURE__*/React.createElement("span", {
        className: styles.facade,
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("span", {
        className: styles.label
      }, label)));
    }
  }, {
    key: "id",
    get: function get() {
      return this.props.id || this._defaultId;
    }
  }, {
    key: "focused",
    get: function get() {
      return isActiveElement(this._input);
    }
  }, {
    key: "checked",
    get: function get() {
      return typeof this.props.checked === 'undefined' ? this.state.checked : this.props.checked;
    }
  }]);

  RadioInput.displayName = "RadioInput";
  return RadioInput;
}(Component), _class2.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  name: PropTypes.string,
  checked: PropTypes.bool,

  /**
   * Whether or not to disable the input
   */
  disabled: PropTypes.bool,

  /**
   * Works just like disabled but keeps the same styles as if it were active
   */
  readOnly: PropTypes.bool,
  variant: PropTypes.oneOf(['simple', 'toggle']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  context: PropTypes.oneOf(['success', 'warning', 'danger', 'off']),
  inline: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func
}, _class2.defaultProps = {
  onClick: function onClick(event) {},
  onChange: function onChange(event) {},
  variant: 'simple',
  size: 'medium',
  disabled: false,
  inline: false,
  context: 'success',
  readOnly: false,
  checked: void 0,
  id: void 0,
  name: void 0,
  value: void 0
}, _temp)) || _class) || _class);
export default RadioInput;
export { RadioInput };